import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import TimePicker from '../../../components/ui/TimePicker';
import DatePicker from '../../../components/ui/DatePicker';

const BookingForm = ({ route, selectedVehicle }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tripType: 'oneway',
    pickupDate: '',
    pickupTime: '',
    returnDate: '',
    returnTime: '',
    pickupLocation: '',
    dropLocation: '',
    passengers: '1',
  });

  const [errors, setErrors] = useState({});
  const [showPickupTimePicker, setShowPickupTimePicker] = useState(false);
  const [showReturnTimePicker, setShowReturnTimePicker] = useState(false);
  const [showPickupDatePicker, setShowPickupDatePicker] = useState(false);
  const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);

  const tripTypeOptions = [
    { value: 'oneway', label: 'One Way Trip' },
    { value: 'roundtrip', label: 'Round Trip' },
    { value: 'local', label: 'Local Tour' },
  ];

  const passengerOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} Passenger${i > 0 ? 's' : ''}`,
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePickupTimeSelect = (time) => {
    handleChange('pickupTime', time);
  };

  const handleReturnTimeSelect = (time) => {
    handleChange('returnTime', time);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.pickupDate) newErrors.pickupDate = 'Pickup date is required';
    if (!formData?.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    if (!formData?.pickupLocation) newErrors.pickupLocation = 'Pickup location is required';
    if (!formData?.dropLocation) newErrors.dropLocation = 'Drop location is required';
    
    if (formData?.tripType === 'roundtrip') {
      if (!formData?.returnDate) newErrors.returnDate = 'Return date is required';
      if (!formData?.returnTime) newErrors.returnTime = 'Return time is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      navigate('/booking-confirmation', {
        state: {
          route,
          vehicle: selectedVehicle,
          bookingDetails: formData,
        },
      });
    }
  };

  const handleWhatsAppInquiry = () => {
    const message = `Hi, I'm interested in booking a cab from ${route?.from} to ${route?.to}. ${selectedVehicle ? `Vehicle: ${selectedVehicle?.name}` : ''}`;
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8 sticky top-24">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground">
            Book Your Journey
          </h3>
          {selectedVehicle && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Selected Vehicle</p>
              <p className="text-sm font-semibold text-foreground">{selectedVehicle?.name}</p>
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Trip Type"
            options={tripTypeOptions}
            value={formData?.tripType}
            onChange={(value) => handleChange('tripType', value)}
            required
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Pickup Date"
              value={formData?.pickupDate}
              onChange={(e) => handleChange('pickupDate', e?.target?.value)}
              error={errors?.pickupDate}
              readOnly
              onClick={() => setShowPickupDatePicker(true)}
              className="cursor-pointer"
            />

            <DatePicker
              isOpen={showPickupDatePicker}
              onClose={() => setShowPickupDatePicker(false)}
              onSelect={(date) => {
                handleChange('pickupDate', date);
                setShowPickupDatePicker(false);
              }}
              initialDate={formData?.pickupDate}
              minDate={new Date()?.toISOString()?.split('T')?.[0]}
            />
            
            <div>
              <Input
                label="Pickup Time"
                type="text"
                value={formData?.pickupTime}
                onClick={() => setShowPickupTimePicker(true)}
                onChange={() => {}}
                error={errors?.pickupTime}
                placeholder="Select time"
                required
                readOnly
                className="cursor-pointer"
              />
            </div>
          </div>
          
          {formData?.tripType === 'roundtrip' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Return Date"
                value={formData?.returnDate}
                onChange={(e) => handleChange('returnDate', e?.target?.value)}
                error={errors?.returnDate}
                readOnly
                onClick={() => setShowReturnDatePicker(true)}
                className="cursor-pointer"
              />

              <DatePicker
                isOpen={showReturnDatePicker}
                onClose={() => setShowReturnDatePicker(false)}
                onSelect={(date) => {
                  handleChange('returnDate', date);
                  setShowReturnDatePicker(false);
                }}
                initialDate={formData?.returnDate}
                minDate={formData?.pickupDate || new Date()?.toISOString()?.split('T')?.[0]}
              />
              
              <div>
                <Input
                  label="Return Time"
                  type="text"
                  value={formData?.returnTime}
                  onClick={() => setShowReturnTimePicker(true)}
                  onChange={() => {}}
                  error={errors?.returnTime}
                  placeholder="Select time"
                  required
                  readOnly
                  className="cursor-pointer"
                />
              </div>
            </div>
          )}
          
          <Input
            label="Pickup Location"
            type="text"
            placeholder="Enter pickup address"
            value={formData?.pickupLocation}
            onChange={(e) => handleChange('pickupLocation', e?.target?.value)}
            error={errors?.pickupLocation}
            required
          />
          
          <Input
            label="Drop Location"
            type="text"
            placeholder="Enter drop address"
            value={formData?.dropLocation}
            onChange={(e) => handleChange('dropLocation', e?.target?.value)}
            error={errors?.dropLocation}
            required
          />
          
          <Select
            label="Number of Passengers"
            options={passengerOptions}
            value={formData?.passengers}
            onChange={(value) => handleChange('passengers', value)}
            required
          />
          
          <div className="pt-4 space-y-3">
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              iconName="Calendar"
              iconPosition="left"
              className="bg-action hover:bg-action/90"
            >
              Proceed to Book
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              iconName="MessageCircle"
              iconPosition="left"
              onClick={handleWhatsAppInquiry}
              className="whatsapp-pulse"
            >
              WhatsApp Inquiry
            </Button>
          </div>
        </form>
        
        <div className="pt-4 border-t border-border space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} className="text-success" />
            <span>Secure booking with instant confirmation</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} className="text-success" />
            <span>24/7 customer support available</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span>Free cancellation up to 24 hours</span>
          </div>
        </div>
      </div>

      <TimePicker
        isOpen={showPickupTimePicker}
        onClose={() => setShowPickupTimePicker(false)}
        onSelect={handlePickupTimeSelect}
        initialTime={formData?.pickupTime}
      />

      <TimePicker
        isOpen={showReturnTimePicker}
        onClose={() => setShowReturnTimePicker(false)}
        onSelect={handleReturnTimeSelect}
        initialTime={formData?.returnTime}
      />
    </div>
  );
};

export default BookingForm;